import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Lock } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Logo } from "@/components/ui/logo";
import { useI18n } from "@/providers/i18n-provider";
import { useVault } from "../vault-context";

/** Pantalla de desbloqueo: pide la contraseña maestra. */
export function VaultUnlock() {
  const { t } = useI18n();
  const vault = useVault();
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await vault.unlock(password());
      setPassword("");
    } catch (err) {
      // crypto lanza Error("invalid master password") en contraseña incorrecta.
      const msgText = err instanceof Error ? err.message : String(err);
      setError(
        msgText.includes("invalid master password") ? t(msg`Incorrect master password.`) : msgText,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center gap-7 px-4">
      <Logo size="lg" />

      {/* Sealed-vault card: the "door" you open with the master key. */}
      <form
        class="animate-scale-in flex w-full flex-col gap-5 rounded-2xl border border-border/60 bg-surface/60 p-6 shadow-[var(--surface-shadow)] backdrop-blur-sm"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <div class="flex flex-col items-center gap-2 text-center">
          <span class="grid h-11 w-11 place-items-center rounded-full bg-accent/12 text-accent ring-1 ring-accent/25">
            <Lock class="h-5 w-5" />
          </span>
          <p class="text-sm text-muted-foreground">{t(msg`Your vault is locked.`)}</p>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="unlock-password">{t(msg`Master password`)}</Label>
          <TextField
            id="unlock-password"
            type="password"
            autocomplete="current-password"
            autofocus
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
        </div>

        <Show when={error()}>
          <p class="text-sm text-destructive" role="alert">
            {error()}
          </p>
        </Show>

        <Button type="submit" loading={busy()} data-testid="vault-unlock-submit">
          {t(msg`Unlock`)}
        </Button>
      </form>
    </div>
  );
}
